// Ground - Parallax ground with biome-based colors
import { memo } from 'react';

const Ground = memo(({ playerX = 0, playerY = 0 }) => {
  const farHillOffset = (playerX * 0.12);
  const midHillOffset = (playerX * 0.2);
  const nearHillOffset = (playerX * 0.35);
  const grassOffset = (playerX * 0.5);
  // Negated: moving forward (playerY decreases) should push hills DOWN toward camera
  const depthShift = -playerY * 0.05;
  
  const getBiomeColors = () => {
    if (playerY > 5000) {
      return {
        base: 'from-amber-400 via-yellow-300 to-cyan-300',
        farHill: ['#D97706', '#F59E0B'],
        midHill: ['#FBBF24', '#FCD34D'],
        nearHill: ['#FDE68A', '#FEF3C7'],
        grass: '#FDE68A',
      };
    }
    if (playerY < -5000) {
      return {
        base: 'from-slate-400 via-blue-200 to-white',
        farHill: ['#475569', '#64748B'],
        midHill: ['#94A3B8', '#CBD5E1'],
        nearHill: ['#E2E8F0', '#F1F5F9'],
        grass: '#E2E8F0',
      };
    }
    if (playerX < -5000) {
      return {
        base: 'from-stone-500 via-amber-200 to-sky-200',
        farHill: ['#78716C', '#A8A29E'],
        midHill: ['#D6D3D1', '#E7E5E4'],
        nearHill: ['#F5F5F4', '#FAFAF9'],
        grass: '#D6D3D1',
      };
    }
    if (playerX > 5000) {
      return {
        base: 'from-pink-400 via-fuchsia-300 to-sky-200',
        farHill: ['#DB2777', '#EC4899'],
        midHill: ['#F472B6', '#F9A8D4'],
        nearHill: ['#FBCFE8', '#FCE7F3'],
        grass: '#FBCFE8',
      };
    }
    return {
      base: 'from-emerald-500 via-green-400 to-sky-200',
      farHill: ['#6BCB77', '#4ECDC4'],
      midHill: ['#4ECDC4', '#22C55E'],
      nearHill: ['#22C55E', '#34D399'],
      grass: '#34D399',
    };
  };
  
  const biome = getBiomeColors();
  
  return (
    <div className="absolute bottom-0 left-0 right-0 h-[65%] overflow-hidden pointer-events-none">
      <div className={`absolute inset-0 bg-gradient-to-t ${biome.base}`} />
      
      <svg className="absolute left-0" style={{ width: '400%', height: '120px', top: `${depthShift * 0.1}%`, transform: `translateX(calc(-50% - ${farHillOffset}px))` }} viewBox="0 0 4000 120" preserveAspectRatio="none">
        <defs><linearGradient id="farHillGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#6BCB77" stopOpacity="0.8" /><stop offset="100%" stopColor="#4ECDC4" stopOpacity="0.4" /></linearGradient></defs>
        <path d="M0,120 C200,80 400,95 650,70 C900,45 1100,85 1350,60 C1600,35 1800,75 2050,50 C2300,25 2500,70 2750,45 C3000,20 3200,65 3450,40 C3700,15 3900,60 4000,50 L4000,0 L0,0 Z" fill="url(#farHillGrad)" />
      </svg>
      
      <svg className="absolute left-0" style={{ width: '350%', height: '100px', top: `${8 + depthShift * 0.15}%`, transform: `translateX(calc(-50% - ${midHillOffset}px))` }} viewBox="0 0 3500 100" preserveAspectRatio="none">
        <defs><linearGradient id="midHillGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.9" /><stop offset="100%" stopColor="#22C55E" stopOpacity="0.6" /></linearGradient></defs>
        <path d="M0,100 C250,60 450,80 700,50 C950,20 1150,70 1400,40 C1650,10 1850,60 2100,30 C2350,0 2550,55 2800,25 C3050,0 3250,50 3500,35 L3500,0 L0,0 Z" fill="url(#midHillGrad)" />
      </svg>
      
      <svg className="absolute left-0" style={{ width: '300%', height: '90px', top: `${15 + depthShift * 0.2}%`, transform: `translateX(calc(-50% - ${nearHillOffset}px))` }} viewBox="0 0 3000 90" preserveAspectRatio="none">
        <defs><linearGradient id="nearHillGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#22C55E" stopOpacity="1" /><stop offset="100%" stopColor="#4ADE80" stopOpacity="0.7" /></linearGradient></defs>
        <path d="M0,90 C300,50 500,70 800,35 C1100,5 1300,55 1600,25 C1900,0 2100,45 2400,20 C2700,0 2900,40 3000,30 L3000,0 L0,0 Z" fill="url(#nearHillGrad)" />
      </svg>
      
      <svg className="absolute left-0" style={{ width: '250%', height: '80px', top: `${22 + depthShift * 0.25}%`, transform: `translateX(calc(-50% - ${nearHillOffset * 1.5}px))` }} viewBox="0 0 2500 80" preserveAspectRatio="none">
        <defs><linearGradient id="closeHillGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#16A34A" stopOpacity="1" /><stop offset="100%" stopColor="#22C55E" stopOpacity="0.85" /></linearGradient></defs>
        <path d="M0,80 C350,40 550,60 850,25 C1150,0 1350,45 1650,15 C1950,0 2150,35 2500,25 L2500,0 L0,0 Z" fill="url(#closeHillGrad)" />
      </svg>
      
      <div className="absolute inset-0 opacity-50" style={{ transform: `translateX(${-grassOffset}px)` }}>
        {Array.from({ length: 80 }).map((_, i) => (
          <div key={i} className="absolute" style={{ left: `${(i * 7) % 200 - 50}%`, top: `${35 + (i * 11) % 55}%` }}>
            <div className="w-1 bg-emerald-700 rounded-full" style={{ height: `${6 + (i % 4) * 3}px`, transform: `rotate(${-10 + (i % 7) * 3}deg)` }} />
          </div>
        ))}
        {Array.from({ length: 40 }).map((_, i) => (
          <div key={`flower-${i}`} className="absolute" style={{ left: `${(i * 9 + 5) % 200 - 50}%`, top: `${38 + (i * 13) % 50}%` }}>
            <div className="rounded-full" style={{ width: `${5 + (i % 3) * 2}px`, height: `${5 + (i % 3) * 2}px`, backgroundColor: ['#FFD93D', '#FF6B6B', '#A66CFF', '#FF69B4', '#4ECDC4'][(i % 5)] }} />
          </div>
        ))}
      </div>
    </div>
  );
});

export default Ground;
